import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  Box,
  Center,
  TextInput,
  Container,
  Text,
  Group,
  Button,
  ActionIcon,
  Badge,
  Table,
  Card,
  UnstyledButton,
  Stack,
  Modal,
  Loader,
  useMantineTheme,
  useMantineColorScheme,
  LoadingOverlay,
  FileButton,
  Collapse,
} from '@mantine/core';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { useTranslation } from 'react-i18next';
import {
  IconPlus,
  IconSettings,
  IconX,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconShield,
  IconUsers,
  IconCheck,
  IconUpload,
  IconPhoto,
} from '@tabler/icons-react';
import { CSSProperties } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { useStore } from '../store/Store';
import { useAppAdminData } from '@/hooks/useAppAdminData';
import { useMasterAdminData } from '@/hooks/useMasterAdminData';
import { AppListingManager } from '@/components/AppListingManager';
import { TableUploader } from '@/components/TableUploader';
import { AppAcquisitionCTA } from '@/components/AppAcquisitionCTA';
import { UserLicenseForm } from '@/components/UserLicenseForm';
import {
  CreateAppSubscriptionData,
  deleteAppSubscription,
  getAppLicensesByAppId,
  getAppsByUser,
  getAppSubscriptionsByAppId,
  // Legacy function removed - using UnifiedAppAPI instead
  patchAppSubscription,
  postAppSubscription,
  UnifiedAppAPI,
} from '@/Api';
import { IApp, IAppLicense, IAppSubscription } from '@/types/App.model';
import { cdnAppLogos } from '@/ApiPaths';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck as IconCheckNotification } from '@tabler/icons-react';

const formatter = new Intl.DateTimeFormat('de-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const EMPTY_USER_LICENSE: CreateAppSubscriptionData = {
  email: '',
  appLicense_id: '',
  expirationDate: '',
};

type UserListActions = {
  add: (appId: string) => void;
  edit: (subscriptionId: string) => void;
  remove: (subscriptionId: string) => void;
};

/** Users of one app, grouped under that app's licenses. Collapsed like product listings. */
function AppUserListing({
  appId,
  appLicenses,
  subscriptions,
  isLoading,
  actionsRef,
  primaryButtonColor,
}: {
  appId: string;
  /** Shared lists loaded once by the admin page for all apps; split here by app. */
  appLicenses: IAppLicense[];
  subscriptions: IAppSubscription[];
  isLoading: boolean;
  actionsRef: React.MutableRefObject<UserListActions | null>;
  primaryButtonColor: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const licenses = useMemo(
    () => appLicenses.filter((license) => license.app_id === appId),
    [appLicenses, appId]
  );
  const usersByLicense = useMemo(
    () =>
      licenses.map((license) => ({
        license,
        users: subscriptions.filter((subscription) => subscription.appLicense_id === license._id),
      })),
    [licenses, subscriptions]
  );
  const userCount = usersByLicense.reduce((count, entry) => count + entry.users.length, 0);
  const canCollapse = licenses.length > 0;

  if (isLoading) {
    return (
      <Box mt="md">
        <Text fw={600} size="sm" mb="xs">
          {t('ecomAdmin.userListings')}
        </Text>
        <Center>
          <Loader size="sm" color="gray" />
        </Center>
      </Box>
    );
  }

  return (
    <Box mt="md">
      {canCollapse ? (
        <Group justify="space-between" align="center" wrap="nowrap">
          <UnstyledButton
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-label={t(open ? 'ecomAdmin.hideUserListings' : 'ecomAdmin.showUserListings')}
            style={{ color: 'inherit' }}
          >
            <Group gap={6} wrap="nowrap" align="center">
              {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              <Text fw={600} size="sm">
                {t('ecomAdmin.userListings')}
              </Text>
              <Text size="xs" c="dimmed" fw={600}>
                {userCount}
              </Text>
            </Group>
          </UnstyledButton>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            style={{ color: primaryButtonColor }}
            onClick={() => actionsRef.current?.add(appId)}
          >
            {t('ecomAdmin.addUser')}
          </Button>
        </Group>
      ) : (
        <Text fw={600} size="sm">
          {t('ecomAdmin.userListings')}
        </Text>
      )}

      {canCollapse ? (
        <Collapse in={open}>
          <Stack gap="sm" mt="sm">
            {usersByLicense.map(({ license, users }) => (
              <Box key={license._id}>
                <Badge variant="light" color="gray" size="sm" mb={6}>
                  {license.licenseName}
                </Badge>
                {users.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    {t('ecomAdmin.noUsersForLicense')}
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {users.map((user) => (
                      <Box
                        key={user._id}
                        p="sm"
                        style={{
                          border: '1px solid var(--mantine-color-default-border)',
                          borderRadius: 8,
                        }}
                      >
                        <Group justify="space-between" align="center" wrap="nowrap">
                          <div style={{ minWidth: 0 }}>
                            <Text size="sm" truncate>
                              {user.email}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatter.format(new Date(user.expirationDate))}
                            </Text>
                          </div>
                          <Group gap="xs" wrap="nowrap">
                            <ActionIcon
                              variant="light"
                              style={{ color: primaryButtonColor }}
                              onClick={() => actionsRef.current?.edit(user._id)}
                              size="sm"
                              aria-label={t('ecomAdmin.editUserLicense')}
                            >
                              <IconSettings size={16} />
                            </ActionIcon>
                            <ActionIcon
                              variant="light"
                              color="red"
                              onClick={() => actionsRef.current?.remove(user._id)}
                              size="sm"
                              aria-label={t('ecomAdmin.deleteUser')}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Collapse>
      ) : (
        <Text size="sm" c="dimmed" mt="xs">
          {t('ecomAdmin.noUserListings')}
        </Text>
      )}
    </Box>
  );
}

/** Local state for app edit modal — typing must not re-render the admin page. */
function EditAppBasicForm({
  initialAppName,
  themeGray,
  primaryButtonColor,
  isSaving,
  onCancel,
  onSave,
}: {
  initialAppName: string;
  themeGray: string;
  primaryButtonColor: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (appName: string, logoFile: File | null) => void;
}) {
  const { t } = useTranslation();
  const [appNameInput, setAppNameInput] = useState(initialAppName);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  return (
    <Stack gap="md">
      <TextInput
        label={t('ecomAdmin.appName')}
        value={appNameInput}
        onChange={(e) => setAppNameInput(e.currentTarget.value)}
        placeholder={t('ecomAdmin.appNamePlaceholder')}
        description={t('ecomAdmin.appNameDescription')}
      />
      <div>
        <Text size="sm" fw={500} mb="xs">
          {t('ecomAdmin.logo')}
        </Text>
        <FileButton
          onChange={(file: File | null) => setLogoFile(file)}
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        >
          {(props) => (
            <Button
              {...props}
              variant="light"
              leftSection={<IconUpload size={16} />}
              style={{ width: '100%' }}
            >
              {logoFile ? logoFile.name : t('ecomAdmin.chooseLogo')}
            </Button>
          )}
        </FileButton>
        {logoFile && (
          <Group gap="xs" mt="xs">
            <IconPhoto size={16} color={themeGray} />
            <Text size="xs" c="dimmed">
              {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
            </Text>
          </Group>
        )}
        <Text size="xs" c="dimmed" mt={4}>
          {t('ecomAdmin.logoUploadHint')}
        </Text>
      </div>
      <Group justify="flex-end" mt="md">
        <Button
          variant="subtle"
          color="gray"
          onClick={onCancel}
          disabled={isSaving}
          style={{
            fontWeight: 500,
            padding: '8px 16px',
          }}
          size="md"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => onSave(appNameInput, logoFile)}
          leftSection={!isSaving && <IconCheck size={18} />}
          style={{
            backgroundColor: primaryButtonColor,
            fontWeight: 500,
            padding: '8px 16px',
          }}
          loading={isSaving}
          disabled={isSaving}
          size="md"
        >
          {isSaving ? t('common.saving') : t('ecomAdmin.saveChanges')}
        </Button>
      </Group>
    </Stack>
  );
}

/** Owns user-modal / search state so opening Add User does not re-render Apps & Licenses. */
const UserManagementSection = React.memo(function UserManagementSection({
  styles,
  appLicenses,
  appSubscriptions,
  setAppSubscriptions,
  primaryButtonColor,
  actionsRef,
}: {
  styles: {
    section: CSSProperties;
    cardHeader: CSSProperties;
    table: CSSProperties;
    tableContainer: CSSProperties;
    tableHeader: CSSProperties;
    tableCell: CSSProperties;
    tableHeaderCell: CSSProperties;
  };
  appLicenses: IAppLicense[];
  appSubscriptions: IAppSubscription[];
  setAppSubscriptions: React.Dispatch<React.SetStateAction<IAppSubscription[]>>;
  primaryButtonColor: string;
  actionsRef: React.MutableRefObject<UserListActions | null>;
}) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingAppSubscriptionIndex, setEditingAppSubscriptionIndex] = useState<number | null>(
    null
  );
  const [deleteAppSubscriptionIndex, setDeleteAppSubscriptionIndex] = useState<number | null>(null);
  // Add is opened from one app's user listing, so only that app's licenses are offered.
  const [addForAppId, setAddForAppId] = useState<string | null>(null);
  const addLicenses = useMemo(
    () =>
      addForAppId ? appLicenses.filter((license) => license.app_id === addForAppId) : appLicenses,
    [addForAppId, appLicenses]
  );
  // General user list is paused. Users are shown under each app license instead.
  // const [userSearchTerm, setUserSearchTerm] = useState('');
  //
  // const licenseNameById = useMemo(() => {
  //   const map = new Map<string, string>();
  //   for (const license of appLicenses) {
  //     map.set(license._id, license.licenseName);
  //   }
  //   return map;
  // }, [appLicenses]);
  //
  // const filteredUsers = useMemo(() => {
  //   const q = userSearchTerm.toLowerCase();
  //   if (!q) return appSubscriptions;
  //   return appSubscriptions.filter(
  //     (subscription) =>
  //       subscription.email.toLowerCase().includes(q) ||
  //       (licenseNameById.get(subscription.appLicense_id) || '').toLowerCase().includes(q)
  //   );
  // }, [appSubscriptions, licenseNameById, userSearchTerm]);
  //
  // const getOriginalIndex = useCallback(
  //   (filteredSubscription: IAppSubscription) =>
  //     appSubscriptions.findIndex((sub) => sub._id === filteredSubscription._id),
  //   [appSubscriptions]
  // );

  const resetForm = useCallback((): void => {
    setShowForm(false);
    setEditingAppSubscriptionIndex(null);
    setAddForAppId(null);
  }, []);

  const handleSaveUserLicense = useCallback(
    async (formData: CreateAppSubscriptionData, editingIndex: number | null): Promise<void> => {
      if (!formData.email || !formData.appLicense_id || !formData.expirationDate) {
        console.error('Form validation failed:', formData);
        alert(t('editProduct.system') + ': ' + t('editProduct.unknownError'));
        return;
      }
      try {
        if (editingIndex !== null) {
          const patchedSubscription = await patchAppSubscription({
            ...appSubscriptions[editingIndex],
            ...formData,
          });

          setAppSubscriptions((prevSubscriptions) => {
            const newSubscriptions = [...prevSubscriptions];
            newSubscriptions[editingIndex] = patchedSubscription;
            return newSubscriptions;
          });

          notifications.show({
            title: t('common.success'),
            message: t('ecomAdmin.userLicenseUpdated'),
            color: 'green',
            icon: <IconCheckNotification size={16} />,
          });
        } else {
          const newSubscription = await postAppSubscription(formData);
          setAppSubscriptions((prevSubscriptions) => [...prevSubscriptions, newSubscription]);

          notifications.show({
            title: t('common.success'),
            message: t('ecomAdmin.userAdded'),
            color: 'green',
            icon: <IconCheckNotification size={16} />,
          });
        }
        resetForm();
      } catch (error: any) {
        console.error('Failed to save user license:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t('ecomAdmin.userLicenseSaveFailed');

        notifications.show({
          title: t('common.error'),
          message: errorMessage,
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      }
    },
    [appSubscriptions, resetForm, setAppSubscriptions, t]
  );

  const handleEdit = useCallback((index: number): void => {
    setEditingAppSubscriptionIndex(index);
    setShowForm(false);
  }, []);

  const handleDelete = useCallback((index: number): void => {
    setDeleteAppSubscriptionIndex(index);
  }, []);

  useEffect(() => {
    actionsRef.current = {
      add: (appId: string) => {
        setEditingAppSubscriptionIndex(null);
        setAddForAppId(appId);
        setShowForm(true);
      },
      edit: (subscriptionId: string) => {
        const index = appSubscriptions.findIndex((subscription) => subscription._id === subscriptionId);
        if (index >= 0) handleEdit(index);
      },
      remove: (subscriptionId: string) => {
        const index = appSubscriptions.findIndex((subscription) => subscription._id === subscriptionId);
        if (index >= 0) handleDelete(index);
      },
    };
    return () => {
      actionsRef.current = null;
    };
  }, [actionsRef, appSubscriptions, handleDelete, handleEdit]);

  const confirmDelete = async () => {
    if (deleteAppSubscriptionIndex !== null) {
      const deleteSuccess = await deleteAppSubscription(
        appSubscriptions[deleteAppSubscriptionIndex]
      );
      if (!deleteSuccess) alert(t('ecomAdmin.subscriptionDeleteFailed'));
      else {
        setAppSubscriptions((prev) =>
          prev.filter((_, index) => index !== deleteAppSubscriptionIndex)
        );
      }
    }
    setDeleteAppSubscriptionIndex(null);
  };

  const editingSubscription =
    editingAppSubscriptionIndex !== null
      ? appSubscriptions[editingAppSubscriptionIndex] ?? null
      : null;

  return (
    <>
      {/* Bottom User Management section stays hidden. Users are listed under each app.
      <Box style={styles.section}>
        <div style={styles.cardHeader}>
          <PageHeader
            icon={<IconUsers size={18} stroke={1.75} />}
            variant="section"
            title={t('ecomAdmin.userManagementTitle')}
            subtitle={t('ecomAdmin.userManagementSubtitle')}
            accentColor="#f59e0b"
            mb={0}
            actions={
              <Button
                leftSection={<IconPlus size={18} />}
                onClick={() => {
                  setEditingAppSubscriptionIndex(null);
                  setShowForm(true);
                }}
                style={{
                  backgroundColor: primaryButtonColor,
                  fontWeight: 500,
                  padding: '8px 16px',
                  height: 'auto',
                }}
                size="md"
              >
                {t('ecomAdmin.addNewUser')}
              </Button>
            }
          />
        </div>
        <Stack gap="md">
      */}
      {/* General user list is paused. Users are listed under each app license.
        <TextInput
          placeholder={t('masterAdmin.users.searchPlaceholder')}
          value={userSearchTerm}
          onChange={(e) => setUserSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          size="sm"
          style={{ width: '100%' }}
        />

        <div style={styles.tableContainer}>
          <Table style={styles.table} striped highlightOnHover>
            <Table.Thead style={styles.tableHeader}>
              <Table.Tr>
                <Table.Th style={styles.tableHeaderCell}>Email</Table.Th>
                <Table.Th style={styles.tableHeaderCell}>License</Table.Th>
                <Table.Th style={styles.tableHeaderCell}>Expiry Date</Table.Th>
                <Table.Th style={styles.tableHeaderCell}>Status</Table.Th>
                <Table.Th style={styles.tableHeaderCell}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredUsers.map((subscription) => {
                const originalIndex = getOriginalIndex(subscription);

                return (
                  <Table.Tr key={subscription._id || originalIndex}>
                    <Table.Td style={styles.tableCell}>{subscription.email}</Table.Td>
                    <Table.Td style={styles.tableCell}>
                      <Badge variant="light" color="gray" size="sm">
                        {licenseNameById.get(subscription.appLicense_id) || 'N/A'}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={styles.tableCell}>
                      {formatter.format(new Date(subscription.expirationDate))}
                    </Table.Td>
                    <Table.Td style={styles.tableCell}>-</Table.Td>
                    <Table.Td style={styles.tableCell}>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          style={{ color: primaryButtonColor }}
                          onClick={() => handleEdit(originalIndex)}
                          size="sm"
                        >
                          <IconSettings size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => handleDelete(originalIndex)}
                          size="sm"
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
        */}

        <Modal
          opened={showForm && editingAppSubscriptionIndex === null}
          onClose={resetForm}
          title={
            <Group>
              <IconUsers size={20} color="#f59e0b" />
              <Text fw={600}>{t('ecomAdmin.addNewUser')}</Text>
            </Group>
          }
          size="md"
        >
          {showForm && editingAppSubscriptionIndex === null && (
            <UserLicenseForm
              key="add-user"
              title=""
              submitButtonText={t('ecomAdmin.addUser')}
              initialValues={EMPTY_USER_LICENSE}
              resetKey={showForm}
              appLicenses={addLicenses}
              onSubmit={(data) => {
                void handleSaveUserLicense(data, null);
              }}
              onCancel={resetForm}
              variant="add"
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </Modal>

        <Modal
          opened={editingAppSubscriptionIndex !== null}
          onClose={resetForm}
          title={
            <Group>
              <IconSettings size={20} color="#3b82f6" />
              <Text fw={600}>{t('ecomAdmin.editUserLicense')}</Text>
            </Group>
          }
          size="md"
        >
          {editingSubscription && editingAppSubscriptionIndex !== null && (
            <UserLicenseForm
              key={editingSubscription._id || editingAppSubscriptionIndex}
              title=""
              submitButtonText={t('ecomAdmin.saveChanges')}
              initialValues={{
                email: editingSubscription.email,
                appLicense_id: editingSubscription.appLicense_id,
                expirationDate: editingSubscription.expirationDate,
              }}
              resetKey={editingAppSubscriptionIndex}
              appLicenses={appLicenses}
              onSubmit={(data) => {
                void handleSaveUserLicense(data, editingAppSubscriptionIndex);
              }}
              onCancel={resetForm}
              variant="edit"
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </Modal>

        <Modal
          opened={deleteAppSubscriptionIndex !== null}
          onClose={() => setDeleteAppSubscriptionIndex(null)}
          title={
            <Group>
              <IconX size={20} color="#ef4444" />
              <Text fw={600} c="red">
                Delete User
              </Text>
            </Group>
          }
          size="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Are you sure you want to delete this user? This action cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setDeleteAppSubscriptionIndex(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmDelete}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      {/*
        </Stack>
      </Box>
      */}
    </>
  );
});

const AdminUserManagement: React.FC = function () {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const isLargeScreen = useMediaQuery('(min-width: 768px)');
  const userProfile = useStore((state) => state.userProfile);
  const {
    isAuthorized: isAppAdminAuthorized,
    userApps,
    isLoading: isAppAdminLoading,
  } = useAppAdminData();
  const { isAuthorized: isMasterAdminAuthorized } = useMasterAdminData();

  // Check if user has any admin permissions (either MasterAdmin or AppAdmin)
  const hasAdminPermissions = isMasterAdminAuthorized || isAppAdminAuthorized;
  const userListActionsRef = useRef<UserListActions | null>(null);
  const [apps, setApps] = useState<IApp[]>([]);
  const [appSubscriptions, setAppSubscriptions] = useState<IAppSubscription[]>([]);
  const [appLicenses, setAppLicenses] = useState<IAppLicense[]>([]);
  const [expandedApps, setExpandedApps] = useState<{ [key: string]: boolean }>({});
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSavingApp, setIsSavingApp] = useState<boolean>(false);
  const [logoRefreshKey, setLogoRefreshKey] = useState<number>(Date.now());

  // Stable style objects — avoid reallocating on every modal/form state change
  const styles = useMemo(
    () => ({
      section: {
        width: '100%',
        maxWidth: '1200px',
        backgroundColor: isDark ? theme.colors.dark[6] : 'white',
        borderRadius: '12px',
        padding: '24px',
        margin: '16px',
        boxShadow: isDark
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
      } as CSSProperties,

      cardHeader: {
        borderBottom: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
        paddingBottom: '16px',
        marginBottom: '24px',
      } as CSSProperties,

      table: {
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
        minWidth: '600px',
      } as CSSProperties,

      tableContainer: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '8px',
        border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
      } as CSSProperties,

      tableHeader: {
        backgroundColor: isDark ? theme.colors.dark[5] : '#f9fafb',
      } as CSSProperties,

      tableCell: {
        padding: '16px 12px',
      } as CSSProperties,

      tableHeaderCell: {
        padding: '16px 12px',
        fontWeight: 600,
        color: isDark ? theme.colors.gray[2] : '#374151',
      } as CSSProperties,

      titleText: {
        margin: 0,
        color: isDark ? theme.colors.gray[2] : '#374151',
      } as CSSProperties,

      subtitleText: {
        marginTop: '4px',
      } as CSSProperties,

      tableStatusStyle: {
        fontSize: '0.8em',
        color: isDark ? theme.colors.gray[4] : '#666',
        backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
        padding: '2px 8px',
        borderRadius: '4px',
      } as CSSProperties,

      tableSectionStyle: {
        marginBottom: '16px',
      } as CSSProperties,

      tableHeaderStyle: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.95em',
        gap: '8px',
        margin: '0 0 10px 0',
        color: isDark ? theme.colors.gray[2] : '#374151',
      } as CSSProperties,
    }),
    [isDark, theme]
  );

  /** Precompute column/row counts so expanded apps do not scan huge tables on every parent render. */
  const appTableStats = useMemo(() => {
    const map = new Map<
      string,
      {
        metadataColumns: number;
        metadataRows: number;
        connectorColumns: number;
        connectorRows: number;
      }
    >();
    for (const app of apps) {
      map.set(app._id, {
        metadataColumns: app.metadata?.columns ? Object.keys(app.metadata.columns).length : 0,
        metadataRows: app.metadata?.rows ? Object.keys(app.metadata.rows).length : 0,
        connectorColumns: app.two_part_connector_selection?.columns
          ? Object.keys(app.two_part_connector_selection.columns).length
          : 0,
        connectorRows: app.two_part_connector_selection?.rows
          ? Object.keys(app.two_part_connector_selection.rows).length
          : 0,
      });
    }
    return map;
  }, [apps]);

  async function getAdminData() {
    try {
      setIsLoading(true);
      const apps = await getAppsByUser(userProfile._id);
      setApps(apps);
      const loaded = await Promise.all(
        apps.map(async (app) => {
          const [licenses, subscriptions] = await Promise.all([
            getAppLicensesByAppId(app._id),
            getAppSubscriptionsByAppId(app._id),
          ]);
          return { licenses, subscriptions };
        })
      );
      setAppLicenses(loaded.flatMap((entry) => entry.licenses));
      setAppSubscriptions(loaded.flatMap((entry) => entry.subscriptions));
      // Update logo refresh key to bust cache
      setLogoRefreshKey(Date.now());
    } catch (error: any) {
      console.error('Failed to load admin data:', error);
      notifications.show({
        title: t('editProduct.system'),
        message: error?.message || t('editProduct.unknownError'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => {
    if (!userProfile._id) return;
    void getAdminData();
  }, [userProfile._id]);

  // Button color for primary actions
  const primaryButtonColor = userProfile.qrCodeColor || theme.primaryColor;

  const toggleApp = useCallback((appId: string) => {
    setExpandedApps((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }));
  }, []);

  const handleEditAppBasicInfo = useCallback((app: IApp): void => {
    setEditingAppId(app._id);
    setLogoError({}); // Reset logo errors when opening edit modal
  }, []);

  const handleSaveAppBasicInfo = useCallback(
    async (appNameInput: string, nextLogoFile: File | null): Promise<void> => {
      if (!editingAppId) return;
      if (isSavingApp) return;

      setIsSavingApp(true);

      const updates: { appName?: string; summaryDescription?: string } = {};
      const trimmedAppName = appNameInput.trim();

      if (trimmedAppName) updates.appName = trimmedAppName;

      if (Object.keys(updates).length === 0 && !nextLogoFile) {
        setIsSavingApp(false);
        notifications.show({
          title: t('ecomAdmin.validationError'),
          message: t('ecomAdmin.validationNeedAppOrLogo'),
          color: 'orange',
          icon: <IconAlertCircle size={16} />,
        });
        return;
      }

      try {
        await UnifiedAppAPI.updateBasic(editingAppId, updates, nextLogoFile || undefined);

        setEditingAppId(null);

        try {
          await getAdminData();
        } catch (refreshError) {
          console.error('Failed to refresh data:', refreshError);
        }

        notifications.show({
          title: t('common.success'),
          message: t('ecomAdmin.appUpdated'),
          color: 'green',
          icon: <IconCheckNotification size={16} />,
        });
      } catch (error: any) {
        console.error('Failed to update app:', error);

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t('ecomAdmin.appUpdateFailed');

        const isPermissionError =
          errorMessage.toLowerCase().includes('permission') ||
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('forbidden') ||
          error?.response?.status === 403 ||
          error?.response?.status === 401;

        notifications.show({
          title: isPermissionError ? t('ecomAdmin.permissionDenied') : t('ecomAdmin.updateFailed'),
          message: isPermissionError
            ? t('ecomAdmin.permissionDeniedMessage')
            : errorMessage,
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      } finally {
        setIsSavingApp(false);
      }
    },
    [editingAppId, isSavingApp, t]
  );

  const cancelAppEdit = useCallback((): void => {
    setEditingAppId(null);
  }, []);

  const editingApp = useMemo(
    () => (editingAppId ? apps.find((app) => app._id === editingAppId) ?? null : null),
    [apps, editingAppId]
  );

  if (!apps || apps.length === 0)
    return (
      <Box style={styles.section}>
        <Text size="lg" ta="center" c="dimmed">
          {t('ecomAdmin.emptyApps')}
        </Text>
      </Box>
    );

  return (
    <Stack gap="md">
      {/* Apps & Licenses Section */}
      <Box style={styles.section}>
        <div style={styles.cardHeader}>
          <PageHeader
            icon={<IconShield size={18} stroke={1.75} />}
            variant="section"
            title={t('ecomAdmin.appsTitle')}
            subtitle={t('ecomAdmin.appsSubtitle')}
            accentColor="#3b82f6"
            mb={0}
          />
        </div>

        <Stack gap="md">
        {apps.map((app) => (
          <Card
            key={app._id}
            withBorder
            p={expandedApps[app._id] ? "md" : "sm"}
            style={{
              border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
              backgroundColor: isDark ? theme.colors.dark[7] : 'white',
              transition: 'padding 0.2s ease',
            }}
          >
            <Group
              style={{ cursor: 'pointer' }}
              onClick={() => toggleApp(app._id)}
              mb={expandedApps[app._id] ? 'sm' : 0}
            >
              {expandedApps[app._id] ? (
                <IconChevronDown size={20} />
              ) : (
                <IconChevronRight size={20} />
              )}
              {logoError[app._id] ? (
                <Box
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '8px',
                    backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
                  }}
                >
                  <IconShield size={24} color={isDark ? theme.colors.gray[6] : '#9ca3af'} />
                </Box>
              ) : (
                <img
                  src={`${cdnAppLogos}${app._id}.png?v=${logoRefreshKey}`}
                  alt={`${app.appName} logo`}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                  onError={() => setLogoError((prev) => ({ ...prev, [app._id]: true }))}
                />
              )}
              <div style={{ flex: 1 }}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text fw={600} size="md">
                    {app.appName}
                  </Text>
                  {expandedApps[app._id] && (
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconSettings size={16} />}
                      style={{ color: primaryButtonColor }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleEditAppBasicInfo(app);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Group>
                <Group gap="xs" mt={4}>
                  {appLicenses
                    .filter((license) => license.app_id === app._id)
                    .map((license, index) => (
                      <Badge key={license._id || index} color="gray" variant="light" size="sm">
                        {license.licenseName}
                      </Badge>
                    ))}
                </Group>
              </div>
            </Group>
            <Collapse in={expandedApps[app._id] || false} transitionDuration={300}>
              <Stack gap="md" mt="md" pl="xl">
                <Group align="flex-start" gap="md">
                  <Box style={{ flex: 1 }}>
                    <Group gap="xs" align="center" wrap="nowrap" mb={app.metadata?.name ? 2 : 'xs'} style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" style={{ ...styles.tableHeaderStyle, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        Metadata Table
                      </Text>
                      {app.metadata?.name && (() => {
                        const fileName = app.metadata.name;
                        const dateMatch = fileName.match(/^(\d{2}-\d{2}-\d{4})_/);
                        const date = dateMatch ? dateMatch[1] : null;
                        const filePart = dateMatch ? fileName.replace(dateMatch[0], '') : fileName;
                        
                        return (
                          <Group 
                            gap={6}
                            style={{
                              fontSize: '0.8em',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                              display: 'inline-flex',
                              alignItems: 'center',
                              alignSelf: 'center',
                              maxWidth: isLargeScreen ? 'none' : '120px',
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            <Text 
                              size="xs" 
                              style={{ 
                                lineHeight: 1.2, 
                                margin: 0, 
                                padding: 0, 
                                display: 'inline', 
                                color: '#000000',
                                overflow: isLargeScreen ? 'visible' : 'hidden',
                                textOverflow: isLargeScreen ? 'clip' : 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: isLargeScreen ? 'none' : '100px',
                              }}
                            >
                              {filePart}
                            </Text>
                          </Group>
                        );
                      })()}
                    </Group>
                    {app.metadata?.name && (() => {
                      const fileName = app.metadata.name;
                      const dateMatch = fileName.match(/^(\d{2}-\d{2}-\d{4})_/);
                      const date = dateMatch ? dateMatch[1] : null;
                      
                      return (
                        <Group gap={4} align="center" mb="xs" style={{ marginTop: '-4px' }}>
                          <IconCalendar 
                            size={10} 
                            style={{ 
                              color: isDark ? theme.colors.gray[5] : theme.colors.gray[6],
                              opacity: 0.5,
                            }} 
                          />
                          <Text 
                            size="xs" 
                            style={{ 
                              lineHeight: 1, 
                              margin: 0, 
                              padding: 0, 
                              color: isDark ? theme.colors.gray[5] : theme.colors.gray[6],
                              opacity: 0.7,
                              fontSize: '0.7em',
                            }}
                          >
                            {date || 'No date'}
                          </Text>
                        </Group>
                      );
                    })()}
                    {app.metadata ? (
                      <Group gap={6} align="center" wrap="wrap" mb="xs">
                        <Group 
                          gap={6}
                          style={{
                            fontSize: '0.8em',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                          }}
                        >
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.violet[6],
                              flexShrink: 0,
                            }}
                          />
                          <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                            {appTableStats.get(app._id)?.metadataColumns ?? 0} Columns
                          </Text>
                        </Group>
                        <Group 
                          gap={6}
                          style={{
                            fontSize: '0.8em',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                          }}
                        >
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.teal[6],
                              flexShrink: 0,
                            }}
                          />
                          <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                            {appTableStats.get(app._id)?.metadataRows ?? 0} Entries
                          </Text>
                        </Group>
                      </Group>
                    ) : (
                      <Group 
                        gap={6}
                        style={{
                          fontSize: '0.75em',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          alignSelf: 'baseline',
                          marginBottom: '8px',
                        }}
                      >
                        <Box
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444',
                            flexShrink: 0,
                            marginTop: '2px',
                          }}
                        />
                        <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                          No Data
                        </Text>
                      </Group>
                    )}
                    <TableUploader
                      appId={app._id}
                      type="metadata"
                      onUploadComplete={() => getAdminData()}
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Group gap="xs" align="center" wrap="nowrap" mb={app.two_part_connector_selection?.name ? 2 : 'xs'} style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" style={{ ...styles.tableHeaderStyle, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        Connector Selection Table
                      </Text>
                      {app.two_part_connector_selection?.name && (() => {
                        const fileName = app.two_part_connector_selection.name;
                        const dateMatch = fileName.match(/^(\d{2}-\d{2}-\d{4})_/);
                        const date = dateMatch ? dateMatch[1] : null;
                        const filePart = dateMatch ? fileName.replace(dateMatch[0], '') : fileName;
                        
                        return (
                          <Group 
                            gap={6}
                            style={{
                              fontSize: '0.8em',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                              display: 'inline-flex',
                              alignItems: 'center',
                              alignSelf: 'center',
                              maxWidth: isLargeScreen ? 'none' : '120px',
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            <Text 
                              size="xs" 
                              style={{ 
                                lineHeight: 1.2, 
                                margin: 0, 
                                padding: 0, 
                                display: 'inline', 
                                color: '#000000',
                                overflow: isLargeScreen ? 'visible' : 'hidden',
                                textOverflow: isLargeScreen ? 'clip' : 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: isLargeScreen ? 'none' : '100px',
                              }}
                            >
                              {filePart}
                            </Text>
                          </Group>
                        );
                      })()}
                    </Group>
                    {app.two_part_connector_selection?.name && (() => {
                      const fileName = app.two_part_connector_selection.name;
                      const dateMatch = fileName.match(/^(\d{2}-\d{2}-\d{4})_/);
                      const date = dateMatch ? dateMatch[1] : null;
                      
                      return (
                        <Group gap={4} align="center" mb="xs" style={{ marginTop: '-4px' }}>
                          <IconCalendar 
                            size={10} 
                            style={{ 
                              color: isDark ? theme.colors.gray[5] : theme.colors.gray[6],
                              opacity: 0.5,
                            }} 
                          />
                          <Text 
                            size="xs" 
                            style={{ 
                              lineHeight: 1, 
                              margin: 0, 
                              padding: 0, 
                              color: isDark ? theme.colors.gray[5] : theme.colors.gray[6],
                              opacity: 0.7,
                              fontSize: '0.7em',
                            }}
                          >
                            {date || 'No date'}
                          </Text>
                        </Group>
                      );
                    })()}
                    {app.two_part_connector_selection ? (
                      <Group gap={6} align="center" wrap="wrap" mb="xs">
                        <Group 
                          gap={6}
                          style={{
                            fontSize: '0.8em',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                          }}
                        >
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.violet[6],
                              flexShrink: 0,
                            }}
                          />
                          <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                            {appTableStats.get(app._id)?.connectorColumns ?? 0} Columns
                          </Text>
                        </Group>
                        <Group 
                          gap={6}
                          style={{
                            fontSize: '0.8em',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                          }}
                        >
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.teal[6],
                              flexShrink: 0,
                            }}
                          />
                          <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                            {appTableStats.get(app._id)?.connectorRows ?? 0} Entries
                          </Text>
                        </Group>
                      </Group>
                    ) : (
                      <Group 
                        gap={6}
                        style={{
                          fontSize: '0.75em',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: isDark ? theme.colors.dark[5] : '#f3f4f6',
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          alignSelf: 'baseline',
                          marginBottom: '8px',
                        }}
                      >
                        <Box
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444',
                            flexShrink: 0,
                            marginTop: '2px',
                          }}
                        />
                        <Text size="xs" style={{ lineHeight: 1.2, margin: 0, padding: 0, display: 'inline', color: '#000000' }}>
                          No Data
                        </Text>
                      </Group>
                    )}
                    <TableUploader
                      appId={app._id}
                      type="connector-selection"
                      onUploadComplete={() => getAdminData()}
                    />
                  </Box>
                </Group>
                <Box>
                  <AppListingManager appId={app._id} />
                  <AppUserListing
                    appId={app._id}
                    appLicenses={appLicenses}
                    subscriptions={appSubscriptions}
                    isLoading={isLoading}
                    actionsRef={userListActionsRef}
                    primaryButtonColor={primaryButtonColor}
                  />
                </Box>
              </Stack>
            </Collapse>
          </Card>
        ))}
      </Stack>
      </Box>

      <UserManagementSection
        styles={styles}
        appLicenses={appLicenses}
        appSubscriptions={appSubscriptions}
        setAppSubscriptions={setAppSubscriptions}
        primaryButtonColor={primaryButtonColor}
        actionsRef={userListActionsRef}
      />

      {/* Edit App Modal */}
      <Modal
        opened={editingAppId !== null}
        onClose={cancelAppEdit}
        title={
          <Group>
            <IconSettings size={20} color="#3b82f6" />
            <Text fw={600}>Edit App Information</Text>
          </Group>
        }
        size="md"
      >
        {editingApp && (
          <EditAppBasicForm
            key={editingApp._id}
            initialAppName={editingApp.appName}
            themeGray={isDark ? theme.colors.gray[4] : '#6b7280'}
            primaryButtonColor={primaryButtonColor}
            isSaving={isSavingApp}
            onCancel={cancelAppEdit}
            onSave={handleSaveAppBasicInfo}
          />
        )}
      </Modal>
    </Stack>
  );
};

/**
 * EcomAdmin: AppAdmin dashboard for customer company administrators
 * AppAdmins can only manage their own apps with limited permissions:
 * - Can change app logo and name
 * - Can manage users within their apps
 * - Cannot manage user limits, model limits, or subscriptions
 * - Cannot add/remove other admins
 *
 * This component provides AppAdmin functionality
 * AppAdmins are different from MasterAdmins (ROBOTOP employees)
 */
export function EcomAdmin() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const { isAuthorized: isAppAdminAuthorized, isLoading: isAppAdminLoading } = useAppAdminData();
  const { isAuthorized: isMasterAdminAuthorized, isLoading: isMasterAdminLoading } =
    useMasterAdminData();

  // Check if user has any admin permissions (either MasterAdmin or AppAdmin)
  const hasAdminPermissions = isMasterAdminAuthorized || isAppAdminAuthorized;
  const isLoading = isAppAdminLoading || isMasterAdminLoading;

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <SessionAuth>
          <Center
          style={{
            minHeight: '100vh',
            backgroundColor: isDark ? theme.colors.dark[7] : '#f8fafc',
          }}
        >
          <LoadingOverlay visible={true} />
        </Center>
      </SessionAuth>
    );
  }

  // Show unauthorized access page if user has no admin permissions
  if (!hasAdminPermissions) {
    return (
      <SessionAuth>
        <Center
          style={{
            minHeight: '100vh',
            backgroundColor: isDark ? theme.colors.dark[7] : '#f8fafc',
            padding: '20px',
          }}
        >
          <AppAcquisitionCTA style={{ margin: '20px auto' }} />
        </Center>
      </SessionAuth>
    );
  }

  return (
    <SessionAuth>
      <Center
        style={{
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[7] : '#f8fafc',
          padding: '20px',
        }}
      >
        <Container size="xl" style={{ width: '100%' }}>
          <AdminUserManagement />
        </Container>
      </Center>
    </SessionAuth>
  );
}
